/* eslint-disable @typescript-eslint/no-explicit-any */

import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { TimeOfDay } from "@/clients/ntas/profiles";
import { LoggerObject } from "@/utils/logger";
import { DeepReadonly } from "@/utils/types";
export const TN_REQUIRED_LENGTH = 11;
export const TN_NO_COUNTRY_CODE = 10;
export const SPP_SUCCESS_CODE = "0000";

/**
 * Calculate the start date for the call history request
 * @param [period] Number of days to get the call log for
 * @returns ISO date string
 */
export function calcStartDate(period: number): string {
    const now: Date = new Date();
    return new Date(now.setDate(now.getDate() - period)).toISOString();
}

/**
 * Wrap phone number in a SIP format
 */
export function formatPuid(tn: string): string {
    return `sip:+${tn}@one.att.net`;
}

/**
 * returns formatted TN with hipen separated and no +1 prefix
 * @param tn 12345678900
 * @returns 234-567-8900
 */
export function formatTNWithHiphens(tn: string): string {
    if (tn && tn.length === TN_REQUIRED_LENGTH) {
        return `${tn.slice(1, 4)}-${tn.slice(4, 7)}-${tn.slice(7)}`;
    }
    return tn;
}

/**
 * returns TN in standard NANP format +1 (NPA) NXX-XXXX
 * @param tn 12345678900
 * @returns +1 (234) 567-8900
 */
export function formattedTN(tn: string): string {
    if (tn && tn.length === TN_REQUIRED_LENGTH) {
        return `+${tn.slice(0, 1)} (${tn.slice(1, 4)}) ${tn.slice(4, 7)}-${tn.slice(7)}`;
    }
    return tn;
}

export function unFormattedTN(tn: string): string {
    return tn.replace(/\D/g, "");
}

export function isValidTN(tn: string): boolean {
    return /^\d{11}$/.test(tn) && tn.startsWith("1");
}

/**
 * Create a date object with the given hours and minutes
 */
export const makeTime = (hours: number, minutes: number): Date => {
    const date: Date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    return date;
};

/**
 * Create a date object with the given UTC hours and minutes
 * @param hours - should be a number from 0 - 23 representing the hour of day in UTC
 * @param minutes - should be a number from 0 - 59 representing the minutes in UTC
 * @returns date object with the given hours and minutes in UTC
 */
export const makeTimeWithUTC = (hours: number, minutes: number): Date => {
    const date: Date = new Date();
    date.setUTCHours(hours, minutes, 0, 0);
    return date;
};

/**
 * Creates a date with given dateObj, timezone and returns TimeOfDay object with UTC hour, minutes
 * @param dateObj - this contains the local time
 * @param timeZone - string which contains the timezone value
 * @returns TimeOfDay object with UTC values
 */
export const convertLocalToGMT = (dateObj: TimeOfDay, timeZone: string): TimeOfDay => {
    if (dateObj.hour != undefined && dateObj.hour >= 0 && dateObj.minute != undefined && dateObj.minute >= 0) {
        const localDate = new Date();
        localDate.setHours(dateObj.hour, dateObj.minute, 0, 0);
        const fmtDate = fromZonedTime(localDate, timeZone); // this is returning local time for the gievn timeZone
        return { hour: fmtDate.getUTCHours(), minute: fmtDate.getUTCMinutes() }; // here we are getting the UTC values from the local time
    }
    return {};
};

/**
 * Creates string that contains timezone offset and time standard name
 * eg: (GMT-07:00) Pacific Daylight Time
 * @param timeZone - string specifing timezone as given in TimeZoneStore
 * @returns string which contains the timezone details
 */
export const getTimeZoneDetailsText = (timeZone: string): string => {
    return formatInTimeZone(new Date(), timeZone, "(OOOO) zzzz");
};

export const dateFormat: string = "MM/dd/yyyy hh:mm a";

//You can add this on the end of a date format to indicate time zone e.g. EDT, PDT
export const timeZoneIndicator: string = "zzz";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface SettledPromises<Args, T, E = any> {
    fulfilled: T[];
    rejected: {
        args: Args;
        reason: E;
    }[];
}
export function removePunctuation(value: string) {
    return value.replace(/[^\w\s]/gi, ""); //remove punctuation
}

/**
 * Non-blockingly waits for all the promises, produced from {@link tasks} to settle & returns an easy-to-process object.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function waitAllSafely<T, Args = any>(tasks: DeepReadonly<(() => [Promise<T>, Args?])[]>) {
    const loggerObj = new LoggerObject("waitAllSafely", undefined);

    return Promise.allSettled(
        tasks.map((t) => {
            const [promise, args] = t();
            return promise.catch((error) => {
                throw [error, args];
            });
        }),
    ).then((results) => {
        const settled: SettledPromises<Args, T> = {
            fulfilled: [],
            rejected: [],
        };
        for (const r of results) {
            if (r.status === "fulfilled") {
                settled.fulfilled.push(r.value);
            } else {
                let rejected: (typeof settled)["rejected"][0];
                if (Array.isArray(r.reason) && r.reason.length === 2) {
                    rejected = {
                        reason: r.reason[0],
                        args: r.reason[1],
                    };
                } else {
                    loggerObj.getLogger().debug(new TypeError("SettledPromise: Unexpected invalid state for error"), r.reason);
                    rejected = {
                        reason: r.reason,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        args: undefined as any,
                    };
                }
                settled.rejected.push(rejected);
            }
        }
        return settled;
    });
}

export function mapLettersToDigits(tn: string): string {
    return tn.toLowerCase().replace(/[a-z]/g, (c: string) => {
        switch (c) {
            case "a":
            case "b":
            case "c":
                return "2";
            case "d":
            case "e":
            case "f":
                return "3";
            case "g":
            case "h":
            case "i":
                return "4";
            case "j":
            case "k":
            case "l":
                return "5";
            case "m":
            case "n":
            case "o":
                return "6";
            case "p":
            case "q":
            case "r":
            case "s":
                return "7";
            case "t":
            case "u":
            case "v":
                return "8";
            case "w":
            case "x":
            case "y":
            case "z":
                return "9";
            default:
                return c;
        }
    });
}

export function formatValidTN(tn: string): string {
    const formattedTN = mapLettersToDigits(tn).replace(/\D/g, "");
    if (formattedTN.length === TN_NO_COUNTRY_CODE) return `1${formattedTN}`;
    if (formattedTN.length === TN_REQUIRED_LENGTH && formattedTN.startsWith("1")) return `${formattedTN}`;
    return "";
}

// // Utility function to flatten an objec with an added cyclic check incase the object we checking is cyclic

export const flattenObject = (obj: any, parent: string = "", res: any = {}, visited = new Set()) => {
    if (visited.has(obj)) {
        return res; // Skip cyclic references
    }
    visited.add(obj);
    for (const key in obj) {
        const propName = parent ? parent + "." + key : key;
        if (typeof obj[key] == "object" && obj[key] !== null) {
            flattenObject(obj[key], propName, res, visited);
        } else {
            res[propName] = obj[key];
        }
    }
    return res;
};

// Utility function to unflatten an object
export const unflattenObject = (data: any) => {
    const result = {};
    for (const i in data) {
        const keys = i.split(".");
        keys.reduce((acc: any, key, idx) => {
            return (acc[key] = acc[key] || (isNaN(Number(keys[idx + 1])) ? (idx === keys.length - 1 ? data[i] : {}) : []));
        }, result);
    }
    return result;
};

export function getValueWithDotNotation(obj: any, dotNotationKey: string) {
    return dotNotationKey.split(".").reduce((acc, key) => {
        return acc ? acc[key] : undefined;
    }, obj);
}
